import type { ReactNode } from 'react';
import { Modal, useDrawer } from './drawer.js';

// In-product confirmation modal — replaces window.confirm so the voice +
// chrome (right-aligned action buttons, plum accent, mag-styled copy)
// match the rest of the app. Caller owns the open state.
//
// Approved spec 748d30e4. This used to hand-roll the portal, the scrim, the
// ESC listener and the body scroll lock, and it mounted and unmounted with no
// animation at all. All four now come from the shared shell; Modal rather than
// Drawer because this is centred, and a destructive-confirmation dialog that
// slides in from the right edge is a different interaction, not an animated
// version of the same one.

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Either a string or a custom node — callers can pass `<>...<b>name</b>...</>`. */
  body: ReactNode;
  /** Defaults to "confirm". Use "delete forever" / "disconnect" / etc. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paint the confirm button in --pulse red. Use for destructive
   *  actions (delete, disconnect, etc.). */
  destructive?: boolean;
  /** True while onConfirm is running — disables both buttons + swaps
   *  the confirm label to "<verb>ing…" via the caller's choice (or
   *  the default progress label). */
  busy?: boolean;
  busyLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/** The `open` prop is kept, so no caller changes.
 *
 *  useDrawer runs its mount effect to flip `visible`, which only happens if
 *  the component mounts when the dialog opens — so the body is split out and
 *  mounted conditionally. Returning null from inside a component that had
 *  already called the hook would leave `visible` stuck true from the previous
 *  open. */
export function ConfirmDialog(props: ConfirmDialogProps) {
  if (!props.open) return null;
  return <ConfirmDialogBody {...props} />;
}

function ConfirmDialogBody({
  title,
  body,
  confirmLabel = 'confirm',
  cancelLabel = 'cancel',
  destructive = false,
  busy = false,
  busyLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { visible, requestClose } = useDrawer(onCancel);

  // BUSY BLOCKS EVERY CLOSE PATH, as it did before: scrim, ESC and the cancel
  // button all route through here. A dialog that can be dismissed while its
  // confirm handler is mid-flight leaves the founder unsure whether the thing
  // they asked for happened.
  const close = () => {
    if (!busy) requestClose();
  };

  const confirmClass = destructive
    ? 'rounded-full bg-[var(--pulse)] px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--cream)] hover:opacity-90 disabled:opacity-50'
    : 'btn btn-spell disabled:opacity-50';

  return (
    <Modal visible={visible} onRequestClose={close} ariaLabelledBy="confirm-dialog-title">
      {/* overflow-hidden is load-bearing, not tidying. The footer below paints
          bg-[var(--paper)] edge to edge and has no radius of its own, so
          without clipping it squares off the two bottom corners this rounds —
          visible as a dialog that is round on top and blunt underneath.
          Clipping on the container fixes it for any child that paints to the
          edge, where a bottom radius on the footer would fix only that one;
          the shadow is unaffected because box-shadow paints outside the box. */}
      <div className="overflow-hidden rounded-2xl border-[1.5px] border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--plum)]">
        <div className="px-5 pt-5">
          <h2
            id="confirm-dialog-title"
            className="font-display text-[20px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]"
          >
            {title}
          </h2>
          <div className="mt-2 text-[13.5px] leading-[1.55] text-[var(--ink)]/75">{body}</div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-3 border-t border-[var(--line)] bg-[var(--paper)] px-5 py-3">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="link-quiet text-[12px] disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={busy}
            className={confirmClass}
          >
            {busy ? (busyLabel ?? `${confirmLabel}…`) : confirmLabel}
            {!busy && !destructive ? <span className="btn-arrow">→</span> : null}
          </button>
        </div>
      </div>
    </Modal>
  );
}
